<template>
  <div class="autocomplete-wrapper">
    <input
        v-model="query"
        @input="fetchPredictions"
        placeholder="Start typing address"
        class="form-input"
        :name="'search-address-' + Math.random()"
        autocomplete="new-password"
    />

    <ul v-if="predictions.length" class="autocomplete-dropdown">
      <li
          v-for="prediction in predictions"
          :key="prediction.place_id"
          @click="selectPrediction(prediction)"
      >
        {{ prediction.description }}
      </li>
    </ul>
  </div>
</template>

<script>
/* global google */
// Google Places address autocomplete. Requires the host app to load the Google
// Maps JS API (with the places library) — both consuming apps already do via a
// <script> tag in their index.html. Theme via the --tou-ac-* CSS custom
// properties (see the <style> block).
export default {
  name: 'AddressAutocomplete',
  emits: ['update:modelValue'],
  data() {
    return {
      query: '',
      predictions: [],
      autocompleteService: null,
      placesService: null
    };
  },
  mounted() {
    const mapDiv = document.createElement('div'); // invisible anchor for PlacesService
    this.autocompleteService = new google.maps.places.AutocompleteService();
    this.placesService = new google.maps.places.PlacesService(mapDiv);
  },
  methods: {
    fetchPredictions() {
      if (!this.query) {
        this.predictions = [];
        this.$emit('update:modelValue', null);
        return;
      }
      this.autocompleteService.getPlacePredictions(
          { input: this.query, types: ['address'] },
          (results, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              this.predictions = results;
            } else {
              this.predictions = [];
            }
          }
      );
    },
    selectPrediction(prediction) {
      this.query = prediction.description;
      this.predictions = [];
      this.placesService.getDetails(
          {
            placeId: prediction.place_id,
            fields: ['address_components', 'formatted_address', 'geometry']
          },
          (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              this.$emit('update:modelValue', this.parsePlace(place));
            }
          }
      );
    },
    reset() {
      this.query = '';
      this.predictions = [];
    },
    setQuery(text) {
      this.query = text || '';
      this.predictions = [];
    },
    parsePlace(place) {
      const getComponent = (type) =>
          place.address_components?.find((c) => c.types.includes(type))?.long_name || '';

      const streetNumber = getComponent('street_number');
      const route = getComponent('route');
      const city = getComponent('locality') || getComponent('sublocality') || '';
      const state = getComponent('administrative_area_level_1');
      const postalCode = getComponent('postal_code');
      const country = getComponent('country');

      return {
        street: `${streetNumber} ${route}`.trim(),
        city,
        state,
        postalCode,
        country,
        formattedAddress: place.formatted_address,
        location: place.geometry?.location?.toJSON?.() ?? null
      };
    }
  }
};
</script>

<style scoped>
/* Theme via --tou-ac-* CSS custom properties; defaults below are neutral. */
.autocomplete-wrapper {
  position: relative;
  width: 100%;
}

.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  background: var(--tou-ac-bg, white);
  border: 1px solid var(--tou-ac-border, #cbd5e1);
  border-top: none;
  border-radius: var(--tou-ac-dropdown-radius, 0);
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
}

.autocomplete-dropdown li {
  padding: 10px;
  cursor: pointer;
  color: var(--tou-ac-text, black);
  font-weight: 400;
  border-bottom: 1px solid var(--tou-ac-item-border, #eee);
}

.autocomplete-dropdown li:last-child {
  border-bottom: none;
}

.autocomplete-dropdown li:hover {
  background-color: var(--tou-ac-hover-bg, #f0f0f0);
}
</style>
